import type { AliasDto, MailboxDetailDto, MailboxSecretDto } from "@bafe/api-client";
import { OPERATIONAL_DEFAULTS } from "@bafe/config";
import { ArrowSquareOutIcon, TrashIcon } from "@phosphor-icons/react";
import {
	Button,
	Card,
	CopyField,
	EmptyState,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Section,
	Select,
	StatusPill,
	useToast,
} from "@bafe/ui";
import {
	checkMailboxPassword,
	formatBytes,
	generateMailboxPassword,
	parseStorageToBytes,
	validateLocalPart,
	validateMailboxQuota,
} from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Popup } from "../../components/Popup";
import { DeviceSetupPanel } from "../../components/DeviceSetupPanel";
import { ErrorPanel } from "../../components/ErrorPanel";
import { PasswordField } from "../../components/PasswordField";
import { QueryState } from "../../components/QueryState";
import { SecretOnce } from "../../components/SecretOnce";
import { TabNav } from "../../components/TabNav";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { rememberWebmailOpenAccount, webmailMailboxInboxUrl } from "../../lib/webmail";
import { formatDateTime, formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";
import { ALIAS_STATUS, MAILBOX_STATUS } from "../../lib/status";
import { MailboxAccess } from "./MailboxAccess";

const RETENTION_DAYS = OPERATIONAL_DEFAULTS.mailbox_deletion_retention_days;

const TABS = ["overview", "access", "aliases", "security", "devices", "advanced"] as const;
type TabId = (typeof TABS)[number];

const TAB_LABEL: Record<TabId, string> = {
	overview: "Overview",
	access: "Access",
	aliases: "Aliases",
	security: "Security",
	devices: "Devices",
	advanced: "Advanced",
};

function isTabId(value: string | null): value is TabId {
	return TABS.includes((value ?? "") as TabId);
}

export function MailboxDetail() {
	const { id = "" } = useParams();
	const [searchParams] = useSearchParams();
	const rawTab = searchParams.get("tab");
	const tab: TabId = isTabId(rawTab) ? rawTab : "overview";

	const mailbox = useQuery({
		queryKey: queryKeys.mailbox(id),
		queryFn: () => api.getMailbox(id),
		enabled: id !== "",
	});

	return (
		<div className="flex flex-col gap-6">
			<QueryState query={mailbox} errorTitle="We could not load this mailbox.">
				{(detail) => (
					<>
						<PageHeader
							title={detail.emailAddress}
							description={detail.displayName || undefined}
							breadcrumb={
								<Link
									to="/mail/mailboxes"
									className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
								>
									Mailboxes
								</Link>
							}
							action={
								<>
									<StatusPill tone={MAILBOX_STATUS[detail.status].tone}>
										{MAILBOX_STATUS[detail.status].label}
									</StatusPill>
									<OpenWebmailButton detail={detail} />
								</>
							}
						/>

						{detail.purgeAfter && (
							<InlineNotice tone="critical" title="Scheduled for deletion">
								This mailbox and its mail are removed permanently after {formatDateTime(detail.purgeAfter)}.
								Contact support before then if this was a mistake.
							</InlineNotice>
						)}

						<TabNav
							label="Mailbox sections"
							currentId={tab}
							items={TABS.map((tabId) => ({
								id: tabId,
								label: TAB_LABEL[tabId],
								to: `/mail/mailboxes/${detail.id}?tab=${tabId}`,
							}))}
						/>

						{tab === "overview" && <OverviewTab detail={detail} />}
						{tab === "access" && <MailboxAccess mailboxId={detail.id} />}
						{tab === "aliases" && <AliasesTab detail={detail} />}
						{tab === "security" && <SecurityTab detail={detail} />}
						{tab === "devices" && (
							<DeviceSetupPanel
								emailAddress={detail.emailAddress}
								username={detail.clientSettings.username}
								settings={{
									imap: detail.clientSettings.imap,
									smtp: detail.clientSettings.smtp,
									jmap: detail.clientSettings.jmap,
								}}
							/>
						)}
						{tab === "advanced" && <AdvancedTab detail={detail} />}
					</>
				)}
			</QueryState>
		</div>
	);
}

function OpenWebmailButton({ detail }: { detail: MailboxDetailDto }) {
	const { toast } = useToast();

	const open = () => {
		if (!detail.stalwartAccountId) {
			toast({ tone: "error", message: "This mailbox is still being set up." });
			return;
		}
		rememberWebmailOpenAccount(detail.id);
		window.open(webmailMailboxInboxUrl(detail.id), "_blank", "noopener,noreferrer");
		// Same-origin session already signs webmail in; handoff only verifies mailbox access.
		void api.createWebmailHandoff(detail.id).catch((error) => {
			const { title, description } = presentError(error);
			toast({
				tone: "error",
				message:
					description ??
					`${title} Add yourself under the Access tab if you need to open this inbox in webmail.`,
			});
		});
	};

	return (
		<Button
			variant="secondary"
			onClick={open}
			iconRight={<ArrowSquareOutIcon className="size-4" aria-hidden="true" />}
		>
			Open Webmail
		</Button>
	);
}

function OverviewTab({ detail }: { detail: MailboxDetailDto }) {
	return (
		<div className="flex flex-col gap-6">
			<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<Fact label="Storage used" value={formatBytes(detail.usedBytes)} />
				<Fact
					label="Storage limit"
					value={detail.quotaBytes === null ? "No limit" : formatBytes(detail.quotaBytes)}
				/>
				<Fact label="Aliases" value={String(detail.aliasCount)} />
				<Fact
					label="Last active"
					value={
						detail.lastActiveAt ? (
							<span
								title={
									detail.lastActiveBy
										? `${formatDateTime(detail.lastActiveAt)} · ${detail.lastActiveBy}`
										: formatDateTime(detail.lastActiveAt)
								}
							>
								{formatRelative(detail.lastActiveAt)}
							</span>
						) : (
							"No activity recorded"
						)
					}
				/>
			</div>

			<DisplayNameForm detail={detail} />
			<QuotaForm detail={detail} />
		</div>
	);
}

function DisplayNameForm({ detail }: { detail: MailboxDetailDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [displayName, setDisplayName] = useState(detail.displayName);

	const save = useMutation({
		mutationFn: () => api.updateMailbox(detail.id, { displayName: displayName.trim() }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			toast({ tone: "success", message: "Display name updated." });
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		save.mutate();
	}

	return (
		<Section title="Display name" description="The name people see when this mailbox sends an email.">
			<form className="flex max-w-lg flex-col gap-3" onSubmit={submit}>
				<Field label="Display name">
					{({ id, describedBy }) => (
						<Input
							id={id}
							value={displayName}
							aria-describedby={describedBy}
							onChange={(event) => setDisplayName(event.target.value)}
						/>
					)}
				</Field>

				{save.isError && <ErrorPanel error={save.error} title="We could not save the display name." />}

				<div>
					<Button
						type="submit"
						variant="primary"
						pending={save.isPending}
						pendingLabel="Saving…"
						disabled={displayName.trim() === detail.displayName}
					>
						Save
					</Button>
				</div>
			</form>
		</Section>
	);
}

type QuotaUnit = "MB" | "GB" | "TB";

function QuotaForm({ detail }: { detail: MailboxDetailDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [value, setValue] = useState(
		detail.quotaBytes === null ? "" : String(Math.round(detail.quotaBytes / 1024 ** 3)),
	);
	const [unit, setUnit] = useState<QuotaUnit>("GB");

	const quotaBytes = value.trim() === "" ? null : parseStorageToBytes(Number(value), unit);
	const check = validateMailboxQuota(
		value.trim() === "" ? null : Number.isFinite(Number(value)) ? quotaBytes : Number.NaN,
		detail.usedBytes,
	);

	const save = useMutation({
		mutationFn: () => api.updateMailbox(detail.id, { quotaBytes }),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			toast({ tone: "success", message: "Storage limit updated." });
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!check.allowed) return;
		save.mutate();
	}

	return (
		<Section
			title="Storage limit"
			description="An administrative ceiling inside your account's shared storage pool. It is not storage bought for this mailbox."
		>
			<form className="flex max-w-lg flex-col gap-3" onSubmit={submit}>
				<div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
					<Field
						label="Limit (optional)"
						error={check.allowed ? null : (check.message ?? null)}
						help="Leave blank for no limit. The account pool still applies."
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								type="number"
								min={1}
								inputMode="numeric"
								value={value}
								placeholder="No limit"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setValue(event.target.value)}
							/>
						)}
					</Field>
					<Field label="Unit">
						{({ id }) => (
							<Select id={id} value={unit} onChange={(event) => setUnit(event.target.value as QuotaUnit)}>
								<option value="MB">MB</option>
								<option value="GB">GB</option>
								<option value="TB">TB</option>
							</Select>
						)}
					</Field>
				</div>

				{save.isError && <ErrorPanel error={save.error} title="We could not save the storage limit." />}

				<div>
					<Button
						type="submit"
						variant="primary"
						pending={save.isPending}
						pendingLabel="Saving…"
						disabled={!check.allowed}
					>
						Save limit
					</Button>
				</div>
			</form>
		</Section>
	);
}

function AliasesTab({ detail }: { detail: MailboxDetailDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [open, setOpen] = useState(false);
	const [localPart, setLocalPart] = useState("");
	const [removing, setRemoving] = useState<AliasDto | null>(null);

	const result = validateLocalPart(localPart);

	function refresh() {
		void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
		void queryClient.invalidateQueries({ queryKey: queryKeys.aliases });
	}

	const create = useMutation({
		mutationFn: () =>
			api.createAlias({
				domainId: detail.domainId,
				localPart: result.normalized,
				destinationType: "mailbox",
				mailboxId: detail.id,
			}),
		onSuccess: (alias) => {
			refresh();
			setLocalPart("");
			setOpen(false);
			toast({ tone: "success", message: `${alias.sourceAddress} now forwards here.` });
		},
	});

	const remove = useMutation({
		mutationFn: (alias: AliasDto) => api.deleteAlias(alias.id),
		onSuccess: () => {
			refresh();
			setRemoving(null);
			toast({ tone: "success", message: "Alias removed." });
		},
	});

	function close() {
		if (create.isPending) return;
		setOpen(false);
	}

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!result.ok) return;
		create.mutate();
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="row head-actions">
				<p className="sub">
					Mail sent to an alias arrives in this mailbox. An alias has no password and nobody signs into it.
				</p>
				<button className="btn" type="button" onClick={() => setOpen(true)}>
					Add alias
				</button>
			</div>

			<Section title="Aliases forwarding here">
				{detail.aliases.length === 0 ? (
					<Card>
						<EmptyState
							title="No aliases yet"
							description="Add an address like sales@ or hello@ and its mail arrives in this mailbox."
							action={
								<Button variant="primary" onClick={() => setOpen(true)}>
									Add alias
								</Button>
							}
						/>
					</Card>
				) : (
					<Card flush>
						<ul>
							{detail.aliases.map((alias) => (
								<li
									key={alias.id}
									className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3 last:border-0"
								>
									<div className="flex min-w-0 flex-wrap items-center gap-2">
										<span className="text-dns text-(--text-primary)">{alias.sourceAddress}</span>
										<span aria-hidden="true" className="text-(--text-muted)">
											→
										</span>
										<span className="sr-only">forwards to</span>
										<span className="text-dns text-(--text-secondary)">
											{alias.destinationLabel || alias.destinationValue}
										</span>
									</div>
									<div className="flex items-center gap-3">
										<StatusPill tone={ALIAS_STATUS[alias.status].tone}>
											{ALIAS_STATUS[alias.status].label}
										</StatusPill>
										<Button size="sm" variant="ghost" onClick={() => setRemoving(alias)}>
											Remove<span className="sr-only"> {alias.sourceAddress}</span>
										</Button>
									</div>
								</li>
							))}
						</ul>
					</Card>
				)}
			</Section>

			<Popup open={open} title="Add alias" onClose={close}>
				<form onSubmit={submit} noValidate>
					<p className="sub">
						Mail to this address on {detail.domainName} arrives in {detail.emailAddress}.
					</p>
					{create.isError && <p className="err">We could not add this alias.</p>}
					{localPart.length > 0 && !result.ok && result.message && <p className="err">{result.message}</p>}
					<label htmlFor="mailbox-alias">New alias</label>
					<div className="row tight">
						<input
							id="mailbox-alias"
							value={localPart}
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							placeholder="sales"
							autoFocus
							onChange={(event) => setLocalPart(event.target.value)}
						/>
						<span className="quiet">@{detail.domainName}</span>
					</div>
					<p className="actions">
						<button className="btn" type="submit" disabled={!result.ok || create.isPending}>
							{create.isPending ? "Adding" : "Add alias"}
						</button>
						<button className="btn ghost" type="button" onClick={close}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title="Remove this alias?"
				description={
					removing
						? `Mail sent to ${removing.sourceAddress} will stop arriving. Messages already delivered stay where they are.`
						: undefined
				}
				confirmLabel="Remove alias"
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>
		</div>
	);
}

function SecurityTab({ detail }: { detail: MailboxDetailDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [password, setPassword] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [secret, setSecret] = useState<MailboxSecretDto | null>(null);

	const check = checkMailboxPassword(password, detail.emailAddress);

	const reset = useMutation({
		mutationFn: () => api.resetMailboxPassword(detail.id, password),
		onSuccess: (result) => {
			setSecret(result);
			setPassword("");
			setSubmitted(false);
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
		},
	});

	const suspend = useMutation({
		mutationFn: () => api.suspendMailbox(detail.id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			toast({ tone: "success", message: "Mailbox suspended. Mail is still stored." });
		},
	});

	const resume = useMutation({
		mutationFn: () => api.resumeMailbox(detail.id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailbox(detail.id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			toast({ tone: "success", message: "Mailbox is active again." });
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitted(true);
		if (!check.ok) return;
		reset.mutate();
	}

	return (
		<div className="flex flex-col gap-6">
			<Section
				title="Reset the mailbox password"
				description="Setting a new password signs this mailbox out of every mail app until the new one is entered."
			>
				{secret ? (
					<Card className="flex flex-col gap-4">
						<SecretOnce
							title={`New password for ${secret.emailAddress}`}
							value={secret.password}
							label="Mailbox password"
						/>
						<div>
							<Button variant="ghost" onClick={() => setSecret(null)}>
								Done
							</Button>
						</div>
					</Card>
				) : (
					<form className="flex max-w-lg flex-col gap-3" onSubmit={submit} noValidate>
						<PasswordField
							label="New mailbox password"
							value={password}
							onChange={setPassword}
							error={submitted && !check.ok ? (check.message ?? null) : null}
							required
							action={
								<button
									type="button"
									className="text-xs font-medium text-(--color-brand-700) underline underline-offset-2"
									onClick={() => setPassword(generateMailboxPassword())}
								>
									Generate secure password
								</button>
							}
						/>

						{reset.isError && <ErrorPanel error={reset.error} title="We could not reset this password." />}

						<div>
							<Button
								type="submit"
								variant="primary"
								pending={reset.isPending}
								pendingLabel="Setting the password…"
							>
								Reset password
							</Button>
						</div>
					</form>
				)}
			</Section>

			<Section
				title="Suspend this mailbox"
				description="Suspending blocks sign-in and sending. Incoming mail and everything already stored are kept."
			>
				{suspend.isError && <ErrorPanel error={suspend.error} title="We could not suspend this mailbox." />}
				{resume.isError && <ErrorPanel error={resume.error} title="We could not resume this mailbox." />}

				<div>
					{detail.status === "suspended" ? (
						<Button
							variant="primary"
							pending={resume.isPending}
							pendingLabel="Resuming…"
							onClick={() => resume.mutate()}
						>
							Resume mailbox
						</Button>
					) : (
						<Button
							variant="secondary"
							pending={suspend.isPending}
							pendingLabel="Suspending…"
							onClick={() => suspend.mutate()}
						>
							Suspend mailbox
						</Button>
					)}
				</div>
			</Section>

			<Section title="Recent credential activity">
				{detail.recentCredentialEvents.length === 0 ? (
					<Card>
						<p className="text-sm text-(--text-secondary)">
							No password changes recorded for this mailbox yet.
						</p>
					</Card>
				) : (
					<Card flush>
						<ul>
							{detail.recentCredentialEvents.map((event) => (
								<li
									key={`${event.action}-${event.createdAt}`}
									className="flex flex-wrap items-baseline justify-between gap-2 border-b border-(--border-subtle) px-4 py-3 last:border-0"
								>
									<span className="text-sm text-(--text-primary)">
										{event.action.replace(/_/g, " ")}
										{event.actorName ? ` by ${event.actorName}` : ""}
									</span>
									<span className="text-xs text-(--text-muted)">{formatDateTime(event.createdAt)}</span>
								</li>
							))}
						</ul>
					</Card>
				)}
			</Section>
		</div>
	);
}

function AdvancedTab({ detail }: { detail: MailboxDetailDto }) {
	return (
		<div className="flex flex-col gap-6">
			<Section
				title="Technical details"
				description="Useful when you contact support. Nothing here needs changing for normal use."
			>
				<Card className="flex flex-col gap-4">
					<LabelledCopy label="Mailbox ID" value={detail.id} />
					<LabelledCopy label="Domain ID" value={detail.domainId} />
					<LabelledCopy label="Provisioning state" value={detail.provisioningState} />
					<LabelledCopy label="Automatic discovery" value={detail.clientSettings.jmap.url} />
					<LabelledCopy label="Created" value={formatDateTime(detail.createdAt)} />
				</Card>
			</Section>

			<DeleteMailbox detail={detail} />
		</div>
	);
}

function DeleteMailbox({ detail }: { detail: MailboxDetailDto }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [open, setOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [typed, setTyped] = useState("");

	const remove = useMutation({
		mutationFn: async () => {
			// Re-authentication first, so a wrong password never reaches the
			// destructive call (spec §20.2).
			await api.reauthenticate(password);
			await api.deleteMailbox(detail.id, typed.trim());
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxes() });
			void queryClient.invalidateQueries({ queryKey: queryKeys.storage });
			toast({
				tone: "success",
				message: `${detail.emailAddress} is queued for deletion. We will email you when it is done.`,
			});
			void navigate("/mail/mailboxes", { replace: true });
		},
	});

	return (
		<Section
			title="Delete this mailbox"
			description="Deleting stops mail arriving and schedules the stored messages for removal."
		>
			<div>
				<Button
					variant="danger"
					onClick={() => setOpen(true)}
					iconLeft={<TrashIcon className="size-4" aria-hidden="true" />}
				>
					Delete mailbox
				</Button>
			</div>

			<ConfirmDialog
				open={open}
				onClose={() => {
					setOpen(false);
					setPassword("");
					setTyped("");
					remove.reset();
				}}
				title={`Delete ${detail.emailAddress}?`}
				description="Mail stops arriving as soon as this is queued."
				confirmLabel="Delete mailbox"
				danger
				pending={remove.isPending}
				error={remove.error}
				confirmPhrase={detail.emailAddress}
				confirmValue={typed}
				onConfirmValueChange={setTyped}
				confirmDisabled={password.trim().length === 0}
				onConfirm={() => remove.mutate()}
			>
				<div className="flex flex-col gap-4">
					<InlineNotice tone="caution" title={`Recoverable for ${RETENTION_DAYS} days`}>
						{formatBytes(detail.usedBytes)} of mail is kept for {RETENTION_DAYS} days. Contact support within
						that window and we can bring it back. After that it is permanently deleted.
					</InlineNotice>

					<InlineNotice tone="info" title="Deletion happens in the background">
						Key queues the work rather than doing it while you wait. You can leave this page.
					</InlineNotice>

					<PasswordField
						label="Your Key password"
						value={password}
						onChange={setPassword}
						autoComplete="current-password"
						help="We ask again because this cannot be undone from the control panel."
						required
					/>
				</div>
			</ConfirmDialog>
		</Section>
	);
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
	return (
		<Card className="flex flex-col gap-1.5">
			<p className="text-xs font-medium uppercase tracking-wide text-(--text-muted)">{label}</p>
			<p className="text-base font-semibold text-(--text-primary)">{value}</p>
		</Card>
	);
}

function LabelledCopy({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<p className="text-xs font-medium text-(--text-muted)">{label}</p>
			<CopyField value={value} label={label} />
		</div>
	);
}
